'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import { useRef } from 'react';
import { ProfileCard, type ProfileCardKpi } from '@/components/profile/profile-card';
import { PodiumHalftoneDecor } from '@/components/social/leaderboard/podium-halftone-decor';
import type { PodiumDataset, PodiumEntry, PodiumUserIdentity } from '@/components/social/types';
import { useFormattedCurrency } from '@/lib/currency';
import { formatCountFor } from '@/lib/format/format-count';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Generic podium — up to top-3 profile cards anchored to the active tab's
// primary metric. KPIs differ per tab:
//   - Traders   → Profit (primary, success-green) · Volume · Trades
//   - Boosters  → Boost Vol. (primary, success-green) · Boosts · Markets
//   - Creators  → Volume (primary, success-green) · Markets · Top Market
//   - Backers   → Backed (primary, success-green) · Backs
//
//   - Desktop: fixed 2-1-3 columns — rank 2 left, rank 1 centre, rank 3 right;
//     missing ranks use a zero-height invisible placeholder (width only) so slot 1
//     stays centred. Rank 1 elevation (`-translate-y`) + bottom alignment apply
//     only when all three ranks are present; otherwise top alignment avoids skew
//     between two cards of different heights.
//   - Tablet (`md`): compact card width + tighter gap so all three cards fit.
//   - Desktop (`lg+`): full card width and wider gap.
//   - Row is flex + justify-center (narrow track), not full-width grid columns.
//   - 0 entries → returns null (existing empty state takes over)
//
// Mobile (< md): the same cards become a horizontal scroll-snap track in rank
// order 1-2-3. A single responsive tree keeps hidden breakpoints from mounting
// duplicate profile, image, and animation observers.
//
// Spec: 071-social-podium-cards
// ---------------------------------------------------------------------------

const PODIUM_TABS = ['leaderboard', 'backers', 'creators', 'boosters'] as const;
type PodiumTab = (typeof PODIUM_TABS)[number];

/** Podium slot width scales from tablet to desktop so cards never clip at md. */
const PODIUM_SLOT_CLASS = 'flex w-64 shrink-0 snap-center justify-center min-w-0 md:w-[224px] md:max-w-[224px] md:snap-none lg:w-[300px] lg:max-w-[300px]' as const; // ARBITRARY: spec 071 podium column width

/** Card fills its podium slot; rank 1 lifts on desktop when all three ranks exist. */
const PODIUM_CARD_CLASS = 'w-full md:max-w-[224px] lg:max-w-[300px]' as const; // ARBITRARY: spec 071

// ---------------------------------------------------------------------------
// PodiumPlaceholder — invisible footprint matching a profile card column so
// rank 1 stays visually centred when ranks 2 and/or 3 are absent.
// ---------------------------------------------------------------------------

/**
 * Width-only footprint so missing ranks keep 2-1-3 columns centred without
 * forcing a tall `aspect-3/4` box (which inflated the row and made `items-end`
 * push shorter real cards down when only two people were on the podium).
 */
function PodiumPlaceholder() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden={true} className="invisible pointer-events-none m-0 h-0 min-h-0 w-full shrink-0 overflow-hidden border-0 p-0" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export interface PodiumProps {
  /** Up to 3 enriched entries — the top-3 rows of `useGetLeaderboard`. */
  entries: PodiumDataset;
  /** Active social-page tab — emitted as `tab` on click analytics. */
  tab: PodiumTab;
}

// ---------------------------------------------------------------------------
// Helper — convert a D6 string to a float for AnimatedNumber consumption.
// Empty / unparseable inputs collapse to 0 so the tween still renders.
// ---------------------------------------------------------------------------

function d6StringToFloat(d6: string): number {
  const n = Number.parseFloat(d6);
  if (!Number.isFinite(n)) return 0;
  return n / 1e6;
}

// ---------------------------------------------------------------------------
// Card builder — turns a podium entry into the ProfileCard prop bundle.
// KPI selection is tab-driven; the primary metric (slot 1) renders in
// success-green via `isPrimary: true`.
// ---------------------------------------------------------------------------

interface PodiumLabels {
  profit: string;
  loss: string;
  volume: string;
  trades: string;
  boostVolume: string;
  boosts: string;
  markets: string;
  marketsCreated: string;
  topMarket: string;
  backed: string;
  backs: string;
  avgBack: string;
}
function buildKpisForTab(entry: PodiumEntry, tab: PodiumTab, labels: PodiumLabels, formatCurrency: (value: number) => string, formatCount: (value: number) => string): readonly [ProfileCardKpi, ProfileCardKpi, ProfileCardKpi] {
  if (tab === 'leaderboard') {
    const pnlValue = d6StringToFloat(entry.totalPnl);
    return [{
      label: pnlValue < 0 ? labels.loss : labels.profit,
      value: pnlValue,
      format: formatCurrency,
      isPrimary: true
    }, {
      label: labels.volume,
      value: d6StringToFloat(entry.totalVolume),
      format: formatCurrency
    }, {
      label: labels.trades,
      value: entry.totalTrades,
      format: formatCount
    }] as const;
  }
  if (tab === 'boosters') {
    return [{
      label: labels.boostVolume,
      value: d6StringToFloat(entry.totalAmountBoosted),
      format: formatCurrency,
      isPrimary: true
    }, {
      label: labels.boosts,
      value: entry.numBoosts,
      format: formatCount
    }, {
      label: labels.markets,
      value: entry.marketsBoosted,
      format: formatCount
    }] as const;
  }
  if (tab === 'backers') {
    const backedTotal = d6StringToFloat(entry.totalBackedNetD6);
    const avgPerBack = entry.numBacks > 0 ? backedTotal / entry.numBacks : 0;
    return [{
      label: labels.backed,
      value: backedTotal,
      format: formatCurrency,
      isPrimary: true
    }, {
      label: labels.backs,
      value: entry.numBacks,
      format: formatCount
    }, {
      label: labels.avgBack,
      value: avgPerBack,
      format: formatCurrency
    }] as const;
  }

  // tab === 'creators'
  return [{
    label: labels.volume,
    value: d6StringToFloat(entry.creatorTotalVolume),
    format: formatCurrency,
    isPrimary: true
  }, {
    label: labels.marketsCreated,
    value: entry.totalMarketsCreated,
    format: formatCount
  }, {
    label: labels.topMarket,
    value: d6StringToFloat(entry.topMarketVolume),
    format: formatCurrency
  }] as const;
}
function buildCardProps(entry: PodiumEntry, rank: 1 | 2 | 3, tab: PodiumTab, labels: PodiumLabels, cta: string, formatCurrency: (value: number) => string, formatCount: (value: number) => string): {
  user: PodiumUserIdentity;
  kpis: readonly [ProfileCardKpi, ProfileCardKpi, ProfileCardKpi];
  profileHref: string;
  ctaLabel: string;
  analyticsContext: {
    tab: PodiumTab;
    rank: 1 | 2 | 3;
  };
} {
  return {
    user: entry.user,
    kpis: buildKpisForTab(entry, tab, labels, formatCurrency, formatCount),
    profileHref: `/portfolio/${entry.user.userId ?? ''}`,
    ctaLabel: cta,
    analyticsContext: {
      tab,
      rank
    }
  };
}

// ---------------------------------------------------------------------------
// PodiumCard — module-scope component (not an inline render-fn) so a parent
// re-render reconciles it in place instead of remounting the card.
// ---------------------------------------------------------------------------

function PodiumCard(t0) {
  const $ = _c(17);
  const {
    entry,
    rank,
    tab,
    labels,
    cta,
    formatKpi,
    formatCount,
    hasFullPodium,
    medalAltFor
  } = t0;
  let t1;
  if ($[0] !== cta || $[1] !== entry || $[2] !== formatCount || $[3] !== formatKpi || $[4] !== labels || $[5] !== rank || $[6] !== tab) {
    t1 = buildCardProps(entry, rank, tab, labels, cta, formatKpi, formatCount);
    $[0] = cta;
    $[1] = entry;
    $[2] = formatCount;
    $[3] = formatKpi;
    $[4] = labels;
    $[5] = rank;
    $[6] = tab;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== medalAltFor || $[9] !== rank) {
    t2 = medalAltFor(rank);
    $[8] = medalAltFor;
    $[9] = rank;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const t3 = rank === 1 && hasFullPodium ? "md:-translate-y-8" : "";
  let t4;
  if ($[11] !== t3) {
    t4 = cn(PODIUM_CARD_CLASS, t3);
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t1 || $[14] !== t2 || $[15] !== t4) {
    t5 = <ProfileCard {...t1} rankMedalAlt={t2} className={t4} />;
    $[13] = t1;
    $[14] = t2;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Podium(t0) {
  const $ = _c(92);
  const {
    entries,
    tab
  } = t0;
  const t = useTranslations("social.podium");
  const {
    formatBalance
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== formatBalance || $[1] !== tab) {
    t1 = value => formatBalance(value, tab === "leaderboard" ? "adaptive" : "compactTight");
    $[0] = formatBalance;
    $[1] = tab;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const formatKpi = t1;
  const t2 = useLocale();
  let t3;
  if ($[3] !== t2) {
    t3 = formatCountFor(t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const formatCount = t3;
  const sectionRef = useRef(null);
  if (entries.length === 0) {
    return null;
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("kpi.profit");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("kpi.loss");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t) {
    t6 = t("kpi.volume");
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = t("kpi.trades");
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t) {
    t8 = t("kpi.boostVolume");
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t) {
    t9 = t("kpi.boosts");
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t) {
    t10 = t("kpi.markets");
    $[17] = t;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t) {
    t11 = t("kpi.marketsCreated");
    $[19] = t;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t) {
    t12 = t("kpi.topMarket");
    $[21] = t;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t) {
    t13 = t("kpi.backed");
    $[23] = t;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t) {
    t14 = t("kpi.backs");
    $[25] = t;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t) {
    t15 = t("kpi.avgBack");
    $[27] = t;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t12 || $[32] !== t13 || $[33] !== t14 || $[34] !== t15 || $[35] !== t4 || $[36] !== t5 || $[37] !== t6 || $[38] !== t7 || $[39] !== t8 || $[40] !== t9) {
    t16 = {
      profit: t4,
      loss: t5,
      volume: t6,
      trades: t7,
      boostVolume: t8,
      boosts: t9,
      markets: t10,
      marketsCreated: t11,
      topMarket: t12,
      backed: t13,
      backs: t14,
      avgBack: t15
    };
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
    $[35] = t4;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const labels = t16;
  let t17;
  if ($[42] !== t) {
    t17 = t("viewProfile");
    $[42] = t;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  const cta = t17;
  let t18;
  if ($[44] !== t) {
    t18 = rank => {
      switch (rank) {
        case 1:
          {
            return t("medal.altFirst");
          }
        case 2:
          {
            return t("medal.altSecond");
          }
        case 3:
          {
            return t("medal.altThird");
          }
      }
    };
    $[44] = t;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  const medalAltFor = t18;
  let t19;
  if ($[46] !== entries) {
    t19 = entries.find(_temp);
    $[46] = entries;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  const rank1Entry = t19;
  let t20;
  if ($[48] !== entries) {
    t20 = entries.find(_temp2);
    $[48] = entries;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  const rank2Entry = t20;
  let t21;
  if ($[50] !== entries) {
    t21 = entries.find(_temp3);
    $[50] = entries;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  const rank3Entry = t21;
  const hasFullPodium = rank1Entry !== undefined && rank2Entry !== undefined && rank3Entry !== undefined;
  let t22;
  if ($[52] !== cta || $[53] !== formatCount || $[54] !== formatKpi || $[55] !== hasFullPodium || $[56] !== labels || $[57] !== medalAltFor || $[58] !== tab) {
    t22 = {
      tab,
      labels,
      cta,
      formatKpi,
      formatCount,
      hasFullPodium,
      medalAltFor
    };
    $[52] = cta;
    $[53] = formatCount;
    $[54] = formatKpi;
    $[55] = hasFullPodium;
    $[56] = labels;
    $[57] = medalAltFor;
    $[58] = tab;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  const cardProps = t22;
  let t23;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="hidden md:block"><PodiumHalftoneDecor sectionRef={sectionRef} /></div>;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  const t24 = hasFullPodium ? "md:items-end" : "md:items-start";
  let t25;
  if ($[61] !== t24) {
    t25 = cn("relative z-10 -mx-4 flex gap-6 overflow-x-auto px-4 scrollbar-none snap-x snap-mandatory md:mx-0 md:w-full md:justify-center md:overflow-visible md:px-0 md:snap-none md:gap-4 lg:gap-6", t24);
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  const t26 = rank1Entry === undefined && "hidden md:flex";
  let t27;
  if ($[63] !== t26) {
    t27 = cn(PODIUM_SLOT_CLASS, "order-1 md:order-2", t26);
    $[63] = t26;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] !== cardProps || $[66] !== rank1Entry) {
    t28 = rank1Entry !== undefined ? <PodiumCard entry={rank1Entry} rank={1} {...cardProps} /> : <PodiumPlaceholder />;
    $[65] = cardProps;
    $[66] = rank1Entry;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== t27 || $[69] !== t28) {
    t29 = <div className={t27} data-podium-slot="1">{t28}</div>;
    $[68] = t27;
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  const t30 = rank2Entry === undefined && "hidden md:flex";
  let t31;
  if ($[71] !== t30) {
    t31 = cn(PODIUM_SLOT_CLASS, "order-2 md:order-1", t30);
    $[71] = t30;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  let t32;
  if ($[73] !== cardProps || $[74] !== rank2Entry) {
    t32 = rank2Entry !== undefined ? <PodiumCard entry={rank2Entry} rank={2} {...cardProps} /> : <PodiumPlaceholder />;
    $[73] = cardProps;
    $[74] = rank2Entry;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] !== t31 || $[77] !== t32) {
    t33 = <div className={t31} data-podium-slot="2">{t32}</div>;
    $[76] = t31;
    $[77] = t32;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  const t34 = rank3Entry === undefined && "hidden md:flex";
  let t35;
  if ($[79] !== t34) {
    t35 = cn(PODIUM_SLOT_CLASS, "order-3", t34);
    $[79] = t34;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  let t36;
  if ($[81] !== cardProps || $[82] !== rank3Entry) {
    t36 = rank3Entry !== undefined ? <PodiumCard entry={rank3Entry} rank={3} {...cardProps} /> : <PodiumPlaceholder />;
    $[81] = cardProps;
    $[82] = rank3Entry;
    $[83] = t36;
  } else {
    t36 = $[83];
  }
  let t37;
  if ($[84] !== t35 || $[85] !== t36) {
    t37 = <div className={t35} data-podium-slot="3">{t36}</div>;
    $[84] = t35;
    $[85] = t36;
    $[86] = t37;
  } else {
    t37 = $[86];
  }
  let t38;
  if ($[87] !== t25 || $[88] !== t29 || $[89] !== t33 || $[90] !== t37) {
    t38 = <section ref={sectionRef} className="relative w-full overflow-visible md:mt-8">{t23}<div data-testid="podium" className={t25}>{t29}{t33}{t37}</div></section>;
    $[87] = t25;
    $[88] = t29;
    $[89] = t33;
    $[90] = t37;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  return t38;
}
function _temp3(e_1) {
  return e_1.rank === 3;
}
function _temp2(e_0) {
  return e_0.rank === 2;
}
function _temp(e) {
  return e.rank === 1;
}